import { Empty, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoFileCategory } from 'types'

import { sortAndAddMissingCategories } from './helpers/documentCoverageHelpers'
import { DocumentCategoryMetric } from './useDataHealthMetrics'

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StatsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
`

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const StatLabel = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const StyledTable = styled(Table<DocumentCategoryMetric>)`
  .ant-table-thead > tr > th {
    background-color: ${({ theme }) => theme.forecast.background2};
  }
`

const CategoryCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const RequiredBadge = styled(Tag)`
  margin: 0;
`

const TABLE_LOCALE = {
  emptyText: (
    <Empty
      description='No documents found for this unit'
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  ),
}

interface DocumentCoverageTableProps {
  metrics: ReadonlyArray<DocumentCategoryMetric>
  totalDocuments: number
  requiredCategories?: ReadonlyArray<GeckoFileCategory>
  isLoading: boolean
}

export const DocumentCoverageTable: FC<DocumentCoverageTableProps> = ({
  metrics,
  totalDocuments,
  requiredCategories = [],
  isLoading,
}) => {
  const columns: ColumnsType<DocumentCategoryMetric> = useMemo(
    () => [
      {
        title: 'Document Category',
        dataIndex: 'categoryLabel',
        key: 'category',
        render: (label: string, record: DocumentCategoryMetric) => {
          const isRequired = requiredCategories.includes(record.category)
          return (
            <CategoryCell>
              <span>{label}</span>
              {isRequired && (
                <RequiredBadge color='blue'>Required</RequiredBadge>
              )}
            </CategoryCell>
          )
        },
      },
      {
        title: 'Count',
        dataIndex: 'count',
        key: 'count',
        width: 120,
        align: 'center',
        render: (count: number, record: DocumentCategoryMetric) => {
          const isRequired = requiredCategories.includes(record.category)
          const isMissing = isRequired && count === 0
          return (
            <Tag color={isMissing ? 'red' : count > 0 ? 'green' : 'default'}>
              {count}
            </Tag>
          )
        },
      },
      {
        title: 'Percentage',
        key: 'percentage',
        width: 120,
        align: 'center',
        render: (_: unknown, record: DocumentCategoryMetric) => {
          if (totalDocuments === 0) return '0%'
          const percentage = ((record.count / totalDocuments) * 100).toFixed(1)
          return `${percentage}%`
        },
      },
    ],
    [requiredCategories, totalDocuments]
  )

  const allMetrics = useMemo(
    () => sortAndAddMissingCategories(metrics, requiredCategories),
    [metrics, requiredCategories]
  )

  const categoryCount = metrics.length
  const requiredCount = requiredCategories.length
  const requiredPresent = useMemo(
    () =>
      requiredCategories.filter((cat) =>
        metrics.find((m) => m.category === cat && m.count > 0)
      ).length,
    [metrics, requiredCategories]
  )

  return (
    <Container>
      <StatsHeader>
        <StatItem>
          <StatLabel>Total Documents</StatLabel>
          <StatValue>{totalDocuments}</StatValue>
        </StatItem>
        <StatItem>
          <StatLabel>Document Categories</StatLabel>
          <StatValue>{categoryCount}</StatValue>
        </StatItem>
        {requiredCount > 0 && (
          <StatItem>
            <StatLabel>Required Categories Present</StatLabel>
            <StatValue>
              {requiredPresent} / {requiredCount}
            </StatValue>
          </StatItem>
        )}
      </StatsHeader>

      <StyledTable
        columns={columns}
        dataSource={allMetrics}
        rowKey='category'
        loading={isLoading}
        pagination={false}
        locale={TABLE_LOCALE}
      />
    </Container>
  )
}
